import { type Action, type History, type Location } from "history";
import { useCallback, useEffect, useRef } from "react";
import { useHistory } from "react-router";
import { type NavigateFunction, useNavigate } from "react-router-dom-v5-compat";

import { showUnsavedChangesPrompt } from "commonComponents/utilities/DirtyTracking/UnsavedChangesPrompt";

export interface IDirtyTrackingNavigationGuardOptions {
    /**
     * Lazily computes the form's dirty state. Called only when a navigation or
     * unload is actually attempted — never during render — so the underlying
     * deep diff (`getObjectDiff`) doesn't run on every keystroke.
     */
    getDirtyState: () => { isDirty: boolean; dirtyKeys: string[] };
    /**
     * Persists the form when the user chooses "Save" on the prompt. Resolve `false` to tell the
     * guard NOT to navigate afterward — either the save didn't happen (e.g. validation failed,
     * stay on the page) or the save performed its own navigation (a save-and-close form that
     * closes itself; the guard must not navigate again). Resolve `true` for a persist-only save
     * so the guard continues the user's original navigation.
     */
    onSave?: () => void | Promise<boolean>;
    onDiscard?: () => void;
    /**
     * Explicit per-form opt-in — when false the guard is a no-op. Enabled on forms
     * that already render the unsaved-changes prompt, to extend that prompt to
     * react-router navigation and browser-unload events.
     */
    enableNavigationGuard: boolean;
}

export interface IDirtyTrackingNavigationGuardResult {
    /**
     * Performs an intentional navigation without triggering the unsaved-changes
     * prompt — an explicit Save redirect, a Cancel/Discard, or relative routing that
     * opens a child route/modal on top of the still-mounted form.
     *
     * It flags the *next* navigation as allowed; the guard's block callback consumes
     * that flag when it fires — synchronously for a forward (PUSH) navigation, and
     * asynchronously (via popstate) for a back (POP) navigation. Because the flag is
     * cleared by the block check itself rather than on a fixed tick, it survives the
     * async POP that a synchronous bypass flag would miss. The guard is never torn
     * down, so it stays armed for whatever the user does next.
     *
     * `performNavigation` receives a `keepGuard` callback. If it ends up NOT
     * navigating, it must call `keepGuard()` so the pending allowance is cleared and
     * the next navigation is still guarded. Navigate synchronously (or via a POP);
     * don't defer the navigation past the current call.
     *
     * Returns whatever `performNavigation` returns (passed straight through), so a
     * save-and-close handler can route its own navigation through here and report the save
     * result to the guard in one call — matching the `onSave` contract (see the third example).
     *
     * @example navigateWithoutPrompt(() => navigate(to))
     * @example navigateWithoutPrompt((keepGuard) => saved ? navigate(to) : keepGuard())
     * @example // save-and-close: persist, let the save navigate, tell the guard not to:
     *   registerGuardSave(() => navigateWithoutPrompt(async () => { await saveAndClose(); return false; }))
     */
    navigateWithoutPrompt: <T extends void | Promise<boolean>>(
        performNavigation: (keepGuard: () => void) => T,
    ) => T;
    /**
     * Overrides the save the guard's "Save" prompt button runs, when the canonical save
     * isn't the one passed as `onSave` (e.g. it lives in an internal handler). Same
     * contract as `onSave`: resolve `false` to tell the guard not to navigate afterward
     * (the save failed, or it navigated itself). Pass `undefined` to clear the override and
     * fall back to `onSave`.
     *
     * Call with the latest closure (e.g. from an effect) so a stale save isn't used.
     */
    registerGuardSave: (save: (() => void | Promise<boolean>) | undefined) => void;
}

/**
 * One mounted form's registration with the shared guard. Holds getters that read the
 * form's latest refs, so the stored entry never goes stale.
 */
interface IGuardEntry {
    getDirtyState: () => { isDirty: boolean; dirtyKeys: string[] };
    save: () => void | Promise<boolean> | undefined;
    discard: () => void;
}

/**
 * The single owner of the app's one allowed block prompt. Holds a stack of mounted form
 * entries (bottom→top in mount order) and the global bits that can't be per-form: the one
 * `history.block` registration, the "allow next navigation" flag, and the navigate used to
 * resume a blocked transition.
 */
interface IGuardOwner {
    entries: IGuardEntry[];
    allowNextNavigation: boolean;
    // True while an unsaved-changes prompt is on screen. Pressing browser Back several times
    // queues multiple POPs, each firing the block; without this, each would stack its own modal.
    promptShowing: boolean;
    navigate: NavigateFunction;
    unblock: () => void;
}

// history v4 allows only ONE active block prompt per history instance. Nested forms (e.g. a
// related-entity modal routed on top of a still-mounted parent form) would otherwise each
// register their own block, with the inner overwriting the outer and never restoring it.
// Instead, every guard shares this one owner and registers as a stack entry.
//
// The app has a single Router, so there is exactly one history and one owner. We build it on
// the first registration and tear it down when the last form unmounts — so it's absent again
// for the next run (this is also what isolates tests, the same way leaving every guarded form
// does in the app: last form gone → owner gone).
let activeOwner: IGuardOwner | undefined;

function handleBlockedNavigation(
    owner: IGuardOwner,
    targetLocation: Location,
    action: Action,
): false | undefined {
    // Intentional navigation (navigateWithoutPrompt, or a prompt's own proceed): allow it
    // and re-arm for whatever comes next.
    if (owner.allowNextNavigation) {
        owner.allowNextNavigation = false;
        return undefined;
    }

    // A prompt is already up (e.g. the user pressed Back several times, queuing POPs). Block
    // this navigation too, but don't stack a second modal on top of the first.
    if (owner.promptShowing) {
        return false;
    }

    // Only the top-most form (the surface the user is on) is consulted. A lower form's
    // dirty state is intentionally ignored while a form is open on top of it: opening that
    // top form was itself a navigation the user chose, so leaving the lower form's edits
    // parked was already their decision. When the top form unmounts, the one beneath it
    // becomes the top and is guarded again.
    const top = owner.entries[owner.entries.length - 1];
    if (!top) {
        return undefined;
    }
    const { isDirty, dirtyKeys } = top.getDirtyState();
    if (!isDirty) {
        return undefined;
    }

    const proceed = () => {
        owner.allowNextNavigation = true;
        // Resume the navigation the user actually attempted. A blocked Back is a POP;
        // navigating to targetLocation would PUSH it as a new forward entry instead of
        // going back, so replay it as a real back.
        if (action === "POP") {
            owner.navigate(-1);
        } else if (action === "REPLACE") {
            owner.navigate(targetLocation, { replace: true });
        } else {
            owner.navigate(targetLocation);
        }
    };

    owner.promptShowing = true;
    showUnsavedChangesPrompt(dirtyKeys, {
        onCloseWithSave: async () => {
            owner.promptShowing = false;
            const saved = await top.save();
            // Stay on the page if the save reported it didn't happen (e.g. invalid).
            if (saved === false) return;
            proceed();
        },
        onCloseWithoutSave: () => {
            owner.promptShowing = false;
            top.discard();
            proceed();
        },
        // User dismissed the prompt (Escape / X / backdrop) and stays on the page — just
        // clear the flag so the next Back re-prompts.
        onDontClose: () => {
            owner.promptShowing = false;
        },
    });
    return false;
}

function registerGuardEntry(
    history: History,
    navigate: NavigateFunction,
    entry: IGuardEntry,
): void {
    if (!activeOwner) {
        const created: IGuardOwner = {
            entries: [],
            allowNextNavigation: false,
            promptShowing: false,
            navigate,
            unblock: () => {},
        };
        created.unblock = history.block((location, blockAction) =>
            handleBlockedNavigation(created, location, blockAction),
        );
        activeOwner = created;
    }
    activeOwner.navigate = navigate;
    activeOwner.entries.push(entry);
}

function unregisterGuardEntry(entry: IGuardEntry): void {
    if (!activeOwner) {
        return;
    }
    const index = activeOwner.entries.indexOf(entry);
    if (index >= 0) {
        activeOwner.entries.splice(index, 1);
    }
    // Last form unmounted — tear down the single block.
    if (activeOwner.entries.length === 0) {
        activeOwner.unblock();
        activeOwner = undefined;
    }
}

/**
 * Prompts the user before navigation when the form is dirty. Covers
 * react-router navigation (history.block) and browser unload events
 * (refresh, tab close).
 *
 * Auto-injected by `withFormikBT`, gated behind the `enableNavigationGuard`
 * per-form opt-in flag. Coexists with `BTModalLayout`'s
 * `modalConfig.beforeClose` flow — they trigger on different events
 * (modal close vs. URL navigation) and don't double-prompt on the
 * common paths.
 *
 * The guard is a safety net for *unintentional* navigation. Explicit exit
 * actions (Save, Cancel/Discard) should wrap their navigation in the returned
 * `navigateWithoutPrompt` so they leave without prompting.
 *
 * Nested forms (a related-entity modal routed on top of a still-mounted parent)
 * are supported: history v4 allows only one block prompt per history, so all guards
 * share one owner and register as a stack. Only the top-most form is consulted — a
 * lower form's dirty edits stay parked while a form is open on top of it (opening it
 * was the user's choice). Each form cleans up its entry on unmount, so the one beneath
 * becomes the top and is guarded again.
 */
export function useDirtyTrackingNavigationGuard({
    getDirtyState,
    onSave,
    onDiscard,
    enableNavigationGuard,
}: IDirtyTrackingNavigationGuardOptions): IDirtyTrackingNavigationGuardResult {
    const history = useHistory();
    const navigate = useNavigate();

    const getDirtyStateRef = useRef(getDirtyState);
    const onSaveRef = useRef(onSave);
    const onDiscardRef = useRef(onDiscard);
    // A registered save (via registerGuardSave) used by the prompt's Save instead of onSave,
    // for forms whose canonical save isn't the one passed as onSave.
    const registeredSaveRef = useRef<(() => void | Promise<boolean>) | undefined>(undefined);

    // Stable entry that always reads the latest refs, so the registered stack entry never
    // goes stale even though it's pushed once on mount.
    const entryRef = useRef<IGuardEntry>({
        getDirtyState: () => getDirtyStateRef.current(),
        save: () => (registeredSaveRef.current ?? onSaveRef.current)?.(),
        discard: () => onDiscardRef.current?.(),
    });

    useEffect(() => {
        getDirtyStateRef.current = getDirtyState;
        onSaveRef.current = onSave;
        onDiscardRef.current = onDiscard;
        // Keep the shared owner's navigate fresh (v5-compat's useNavigate changes identity
        // when the location changes).
        if (activeOwner) {
            activeOwner.navigate = navigate;
        }
    }, [getDirtyState, onSave, onDiscard, navigate]);

    // Warn on browser tab close / refresh. Per-hook: unlike history.block, beforeunload
    // supports multiple listeners, so each mounted form can guard its own dirty state.
    useEffect(() => {
        if (!enableNavigationGuard) {
            return;
        }
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            // A leave the user already confirmed is in flight (navigateWithoutPrompt, or a
            // prompt's Save/Don't-save). If it resolves to a hard load
            if (activeOwner?.allowNextNavigation) {
                return;
            }
            if (getDirtyStateRef.current().isDirty) {
                e.preventDefault();
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [enableNavigationGuard]);

    // Register this form as a stack entry on the shared owner.
    useEffect(() => {
        if (!enableNavigationGuard || !history) {
            return;
        }
        const entry = entryRef.current;
        registerGuardEntry(history, navigate, entry);
        return () => unregisterGuardEntry(entry);
        // navigate is intentionally excluded — the ref-sync effect keeps owner.navigate
        // current without re-registering the entry on every navigation.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history, enableNavigationGuard]);

    const navigateWithoutPrompt = useCallback(
        <T extends void | Promise<boolean>>(performNavigation: (keepGuard: () => void) => T) => {
            // Flag the upcoming navigation as intentional on the shared owner; the block
            // callback consumes the flag when it fires. Don't clear it on return — a POP
            // fires its block check asynchronously. If `performNavigation` doesn't navigate,
            // it must call keepGuard() to clear the pending allowance itself.
            if (activeOwner) {
                activeOwner.allowNextNavigation = true;
            }
            return performNavigation(() => {
                if (activeOwner) {
                    activeOwner.allowNextNavigation = false;
                }
            });
        },
        [],
    );

    const registerGuardSave = useCallback((save: (() => void | Promise<boolean>) | undefined) => {
        registeredSaveRef.current = save;
    }, []);

    return { navigateWithoutPrompt, registerGuardSave };
}
